/**
 * The visitor's location, shared by /near-me and the clinic directory.
 *
 * Two sources, in order of trust:
 *   gps - the browser's geolocation API, granted by a tap. Exact.
 *   ip  - Vercel's IP geolocation via /api/geo. City-level, no prompt, used as
 *         the first-visit default so results are already sorted around the
 *         visitor's city. Pages hide per-clinic distances for this source.
 */
export type LocationSource = 'gps' | 'ip';

export interface SavedLocation {
  lat: number;
  lng: number;
  source?: LocationSource;
  /** Human label for the approximate source, e.g. "Noida". */
  label?: string;
}

const KEY = 'userLocation';

/**
 * Read the saved location. Returns null when absent or unreadable - private
 * windows and blocked site data both throw on access.
 */
export function readSavedLocation(): SavedLocation | null {
  try {
    const stored = localStorage.getItem(KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (typeof parsed?.lat === 'number' && typeof parsed?.lng === 'number') {
      return {
        lat: parsed.lat,
        lng: parsed.lng,
        source: parsed.source === 'ip' ? 'ip' : 'gps',
        label: typeof parsed.label === 'string' ? parsed.label : undefined,
      };
    }
  } catch {
    /* no stored location */
  }
  return null;
}

export function writeSavedLocation(loc: SavedLocation): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(loc));
  } catch {
    /* storage unavailable */
  }
}

export function clearSavedLocation(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* storage unavailable */
  }
}

interface GeoResponse {
  located: boolean;
  city: string | null;
  lat: number | null;
  lng: number | null;
}

/**
 * City-level location from the request IP (same-origin edge function). Null
 * when unavailable: local dev, prerender, blocked, or an unlocatable IP. Never
 * called during prerender so no visitor's city is baked into a static page.
 */
export async function fetchApproxLocation(): Promise<SavedLocation | null> {
  if ((window as unknown as { __PRERENDER__?: boolean }).__PRERENDER__) return null;
  try {
    const res = await fetch('/api/geo', { headers: { accept: 'application/json' } });
    if (!res.ok) return null;
    const data = (await res.json()) as GeoResponse;
    if (!data.located || data.lat == null || data.lng == null) return null;
    return { lat: data.lat, lng: data.lng, source: 'ip', label: data.city ?? undefined };
  } catch {
    return null;
  }
}
